import { inject, Injectable } from '@angular/core';
import { SecurityService } from '@core/services';
import { TransactionType } from '@shared/helpers/enums/transaction-type';
import { Mapper } from '@shared/helpers/mapper';
import { Security, SecurityTaxResultItem } from '@shared/models/security';
import { SecurityReviewResult } from '@shared/models/security/security-review-result';
import { HeaderService } from '@shared/services';
import { forkJoin, map, Observable, of } from 'rxjs';
import {
  SecurityQuestionAnswer,
  UpdateSecurityQuestionAnswerRequest,
  UpdateSecurityRequest,
  UpdateSecurityTaxRateRequest,
} from 'src/app/web-api-client';
import { SecurityReviewEditionCache } from '../abstractions/security-review-edition-cache';
import { ApprovalSecurityStatus } from '../helpers/enums/approval-security-status';
import { SecurityTaxResultItemForm } from '../models/edit-tax-result-approval/security-tax-result-item-form';

@Injectable({
  providedIn: 'root',
})
export class TaxResultEditionService {
  private readonly securityService = inject(SecurityService);
  private readonly headerService = inject(HeaderService);

  analyzeResponse(
    securitiesResponse: SecurityReviewResult[],
    securities: Security[],
    cache: SecurityReviewEditionCache
  ): Observable<SecurityReviewResult[]> {
    const ids: number[] = securities.map(
      (security: Security) => security.securityId
    );
    const idsToUpdate: number[] = [];

    securitiesResponse.forEach(
      ({ status, securityId, message }: SecurityReviewResult) => {
        if (status === ApprovalSecurityStatus.Ok) {
          if (securityId && ids.includes(securityId)) {
            idsToUpdate.push(securityId);
          }
        } else if (
          status === ApprovalSecurityStatus.BadRequest ||
          status === ApprovalSecurityStatus.Forbidden
        ) {
          console.error(status);
          console.error(message);
        }
      }
    );

    if (idsToUpdate.length === 0) {
      return of(securitiesResponse);
    }
    return forkJoin(
      this.buildUpdateRequest(
        securities.filter(security =>
          idsToUpdate.includes(security.securityId)
        ),
        cache
      )
    ).pipe(
      map(() => {
        return securitiesResponse;
      })
    );
  }

  updateSecurities(
    securities: Security[],
    cache: SecurityReviewEditionCache
  ): Observable<void[]> {
    return forkJoin(this.buildUpdateRequest(securities, cache));
  }

  private buildUpdateRequest(
    securities: Security[],
    cache: SecurityReviewEditionCache
  ): Observable<void>[] {
    const requests = Mapper.mapByList(securities, UpdateSecurityRequest, [
      'securityId',
      'name',
      'isActive',
      'isinCusip',
      {
        entityId: (source: Security): number =>
          this.isGeneralRates(source) ? 0 : (source.entity?.entityId ?? 0),
        clientId: (source: Security): number | undefined =>
          this.isGeneralRates(source)
            ? (source.entity?.client?.clientId ??
              this.headerService.selectedClient()?.id)
            : undefined,
        investmentJurisdictionId: (source: Security): number =>
          source.investmentJurisdiction.investmentJurisdictionId,
        securityTypeId: (source: Security): number | undefined =>
          source.securityType.securityTypeId,
        transactionTypeId: (source: Security): number | undefined =>
          source.transactionType.transactionTypeId,
        generalRateEntityTypeId: (source: Security): number | undefined =>
          this.isGeneralRates(source)
            ? source.generalRateEntityTypeId
            : undefined,
        acquisitionDate: (source: Security): Date | undefined =>
          source.acquisitionDate,
        taxRates: (source: Security): UpdateSecurityTaxRateRequest[] =>
          this.generateTaxRatesRequestList(source, cache),
        questionAnswers: (
          source: Security
        ): UpdateSecurityQuestionAnswerRequest[] =>
          Mapper.mapByList(
            source.securityQuestionAnswers ?? [],
            UpdateSecurityQuestionAnswerRequest,
            [
              {
                answer: (source: SecurityQuestionAnswer): string =>
                  source.text.toString(),
                taxPolicyId: (
                  source: SecurityQuestionAnswer
                ): number | undefined => source.taxPolicy?.taxPolicyId,
                taxRuleId: (
                  source: SecurityQuestionAnswer
                ): number | undefined => source.taxRuleId,
                questionId: (
                  source: SecurityQuestionAnswer
                ): number | undefined => source.question?.questionId,
                isActive: (
                  source: SecurityQuestionAnswer
                ): boolean | undefined => source.question?.isActive,
                unsureAnswer: (
                  source: SecurityQuestionAnswer
                ): boolean | undefined => source.unsureAnswer,
              },
            ]
          ),
        comment: (source: Security): string => source.comment,
        adminComment: (source: Security): string | undefined =>
          source.adminComment,
        skipNotification: true,
      },
    ]);

    return requests.map(req =>
      this.securityService.updateSecurity(req.securityId!, req)
    );
  }

  private generateTaxRatesRequestList(
    security: Security,
    cache: SecurityReviewEditionCache
  ): UpdateSecurityTaxRateRequest[] {
    let taxRates: SecurityTaxResultItem[] = [];

    taxRates = taxRates.concat(
      security.securityTaxResultItems.length > 0
        ? security.securityTaxResultItems
        : []
    );

    if (cache.toAdd.length > 0) {
      const items = cache.toAdd.filter(
        field => field.metadata.securityId === security.securityId
      );
      taxRates = taxRates.concat(
        items.length > 0 ? this.createNewsTaxRates(items) : []
      );
    }

    if (cache.toDelete.length > 0) {
      const items = cache.toDelete.filter(
        field => field.metadata.securityId === security.securityId
      );

      taxRates =
        items.length > 0 ? this.deleteTaxRates(taxRates, items) : taxRates;
    }

    return Mapper.mapByList(taxRates, UpdateSecurityTaxRateRequest, [
      'taxRuleId',
      'taxPolicyId',
      {
        rate: ({
          securityTaxResultItemRate,
          taxResultItemRate,
        }: SecurityTaxResultItem): number | null => {
          if (typeof securityTaxResultItemRate === 'number') {
            return securityTaxResultItemRate;
          }
          return taxResultItemRate;
        },
        description: source => source.securityTaxResultItemDescription,
        securityTaxResultItemId: source => source.securityTaxResultItemId,
        taxResultItemId: null, // Remove default tax results to avoid overriding
        taxResultItemTypeId: source => source.taxResultItemTypeId,
        isActive: true,
      },
    ]);
  }

  private createNewsTaxRates(
    itemsToAdd: SecurityTaxResultItemForm[]
  ): SecurityTaxResultItem[] {
    return Mapper.mapByList(itemsToAdd, SecurityTaxResultItem, [
      {
        securityTaxResultItemRate: (itemField): number =>
          parseFloat(itemField.rate!.value()),
        taxResultItemRate: (itemField): number =>
          parseFloat(itemField.rate!.value()),
        taxResultItemTypeId: (itemField): number =>
          itemField.metadata.taxResultItemTypeId!,
        securityTaxResultItemDescription: (itemField): string =>
          itemField.explanation!.value(),
      },
    ]);
  }

  private deleteTaxRates(
    taxResultItems: SecurityTaxResultItem[],
    itemsToDelete: SecurityTaxResultItemForm[]
  ): SecurityTaxResultItem[] {
    return taxResultItems.filter((taxResultItem: SecurityTaxResultItem) => {
      return !itemsToDelete
        .map(item => item.metadata.securityTaxResultItemId)
        .includes(taxResultItem.securityTaxResultItemId);
    });
  }

  private isGeneralRates(source: Security): boolean {
    return (
      source.transactionType?.transactionTypeId === TransactionType.GeneralRates
    );
  }
}
